import styled from '@emotion/styled'
import { ExtranetCustomerGroupScheduleSlotDto } from '@pharmac-extranet/extranet-api'
import {
  PharmacBadge,
  PharmacButtonBase,
  PharmacCalendarDay,
  PharmacIcon,
  PharmacText,
} from '@pharmac/ui'
import { useBreakpoint, useLanguage } from '../../hooks'
import { ExtranetContainer } from '../ExtranetContainer'

const WeekdayLabel = styled(PharmacText)`
  text-align: center;
  font-weight: 600;
  padding-bottom: var(--spacing-small);
`
const Day = styled(PharmacCalendarDay)<{ isDesktop?: boolean }>`
  display: ${(props) => (props.isDesktop ? 'none' : '')};
  border: 1px solid var(--color-accent-4);
  border-radius: var(--border-radius-tiniest);
  background-color: var(--color-accent-1);

  &:hover {
    transform: none;
    box-shadow: none;
  }
`

const ScheduleIcon = styled(PharmacIcon)<{ disabled?: boolean }>`
  color: var(--color-accent-1);
  position: absolute;
  left: var(--spacing-small);
  top: 5px;
  color: ${(props) => (props.disabled ? 'var(--color-accent-1)' : 'var(--color-accent-7)')};
`

type ScheduleSlotProps = {
  selected: boolean
  isCustomerSelectionMode: boolean
  isCustomerSelectedScheduleSlot: boolean
  isError: boolean
}

const ScheduleSlot = styled(PharmacButtonBase)<ScheduleSlotProps>`
  position: relative;
  border: none;
  background: ${(props) =>
    props.isError
      ? 'var(--color-danger)'
      : props.selected && !props.isCustomerSelectionMode
      ? 'var(--color-success)'
      : props.isCustomerSelectedScheduleSlot
      ? 'var(--color-success)'
      : 'var(--color-warning)'};
  display: inline-flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  font-size: var(--typo-text-small-size);
  font-family: var(--font-family-primary);
  font-weight: 700;
  padding: var(--badge-padding);
  border-radius: var(--border-radius-tiniest);
  color: ${(props) => (props.disabled ? 'var(--color-accent-1)' : 'var(--color-accent-7)')};
  margin-bottom: 2px;
  cursor: pointer;
  &:hover {
    transition: none;
    transform: scale(1.05);
    z-index: 10;
    box-shadow: ${() =>
      `2.8px 2.8px 2.2px rgba(0, 0, 0, 0.02), 6.7px 6.7px 5.3px rgba(0, 0, 0, 0.028),
        12.5px 12.5px 10px rgba(0, 0, 0, 0.035), 22.3px 22.3px 17.9px rgba(0, 0, 0, 0.042),
        41.8px 41.8px 33.4px rgba(0, 0, 0, 0.05), 100px 100px 80px rgba(0, 0, 0, 0.07);`};
  }
  &:disabled {
    cursor: not-allowed;
    background: var(--color-accent-4);
  }
`

const weekdays = {
  'fi-FI': {
    0: 'MA',
    1: 'TI',
    2: 'KE',
    3: 'TO',
    4: 'PE',
    5: 'LA',
    6: 'SU',
  },
}

interface DeliveryRouteScheduleDto {
  deadlineTime: string
  moveToProductionTime: string
  moveToProductionDayOffset: number
  productionTime: string
  productionDayOffset: number
  imagingTime: string
  imagingDayOffset: number
  inspectionTime: string
  inspectionDayOffset: number
  sendingTime: string
  sendingDayOffset: number
  deliveredDayOffset: number
  isActive: boolean
}

interface UrgentDeliveryRouteScheduleDto {
  deadlineTime: string
  moveToProductionTime: string
  moveToProductionDayOffset: number
  productionTime: string
  productionDayOffset: number
  imagingTime: string
  imagingDayOffset: number
  inspectionTime: string
  inspectionDayOffset: number
  sendingTime: string
  sendingDayOffset: number
  deliveredDayOffset: number
}

interface DeliveryRouteDto {
  id: number
  name: string
  schedules: DeliveryRouteScheduleDto[]
  urgentSchedule: UrgentDeliveryRouteScheduleDto
}

export type ScheduleSlotDayAction = 'ADD' | 'REMOVE'

type Props = {
  index: number
  scheduleSlots: ExtranetCustomerGroupScheduleSlotDto[] | undefined
  deliveryRoute: DeliveryRouteDto | undefined
  onSelect: (slot: ExtranetCustomerGroupScheduleSlotDto) => void
  isCustomerSelectionMode?: boolean
  selectedSlotId?: number
}

export const ExtranetScheduleSlotPicker = ({
  index,
  scheduleSlots,
  deliveryRoute,
  onSelect,
  isCustomerSelectionMode = false,
  selectedSlotId,
}: Props) => {
  const { minDesktop } = useBreakpoint()
  const { t } = useLanguage()

  if (!scheduleSlots) return

  const cycleIndexDays = scheduleSlots
    .filter((e) => e.cycleDateIndex === index)
    .sort((a, b) => a.deadlineTime.localeCompare(b.deadlineTime))

  const schedules =
    deliveryRoute && deliveryRoute.schedules?.length > 0 ? deliveryRoute.schedules : []

  const scheduleSlotIds = scheduleSlots.map((a) => a.id)

  return (
    <Day
      key={index}
      isDesktop={!minDesktop && cycleIndexDays.length === 0}
    >
      {minDesktop && (
        <WeekdayLabel>
          {
            weekdays['fi-FI'][
              index < 7
                ? (index as keyof (typeof weekdays)['fi-FI'])
                : ((index - 7) as keyof (typeof weekdays)['fi-FI'])
            ]
          }
        </WeekdayLabel>
      )}

      {cycleIndexDays && cycleIndexDays.length > 0 ? (
        cycleIndexDays.map((day) => {
          const isScheduleSlotFound = scheduleSlotIds.includes(day.id)

          const isCustomerSelectedScheduleSlotId =
            isScheduleSlotFound && selectedSlotId !== undefined && day.id === selectedSlotId

          const isScheduleFoundForTime = !!schedules.find(
            (schedule) => schedule.deadlineTime === day.deadlineTime
          )

          const isSlotActiveWhenItIsNotInSchedule =
            (!isCustomerSelectionMode && isScheduleSlotFound && !isScheduleFoundForTime) ||
            (isCustomerSelectionMode && !isScheduleSlotFound)

          const isSelected = isScheduleSlotFound || isCustomerSelectedScheduleSlotId
          const isDisabled =
            ((isCustomerSelectionMode && !isScheduleSlotFound) || !isScheduleFoundForTime) &&
            (!isSlotActiveWhenItIsNotInSchedule ||
              (isSlotActiveWhenItIsNotInSchedule && isCustomerSelectionMode))

          const isError = isSlotActiveWhenItIsNotInSchedule

          return (
            <ScheduleSlot
              isCustomerSelectionMode={isCustomerSelectionMode}
              isCustomerSelectedScheduleSlot={isCustomerSelectedScheduleSlotId}
              disabled={isDisabled}
              isError={isError}
              selected={isSelected}
              title={isSlotActiveWhenItIsNotInSchedule ? ' ' : undefined}
              onClick={() => onSelect(day)}
              key={day.id}
            >
              {((isScheduleSlotFound && !isCustomerSelectionMode) ||
                isCustomerSelectedScheduleSlotId) &&
                !isSlotActiveWhenItIsNotInSchedule && (
                  <ScheduleIcon
                    disabled={isDisabled}
                    size="16px"
                    icon="checkCircle"
                  />
                )}
              {(!isScheduleSlotFound || isCustomerSelectionMode) &&
                !isSlotActiveWhenItIsNotInSchedule && (
                  <ScheduleIcon
                    disabled={isDisabled}
                    size="16px"
                    icon="circle"
                  />
                )}
              {isSlotActiveWhenItIsNotInSchedule && (
                <ScheduleIcon
                  disabled={isDisabled}
                  size="16px"
                  icon="alertTriangle"
                />
              )}
              <ExtranetContainer>
                {!minDesktop && (
                  <WeekdayLabel marginSmallRight>
                    {
                      weekdays['fi-FI'][
                        index < 7
                          ? (index as keyof (typeof weekdays)['fi-FI'])
                          : ((index - 7) as keyof (typeof weekdays)['fi-FI'])
                      ]
                    }
                  </WeekdayLabel>
                )}
                <PharmacText weight="bold">{day.deadlineTime}</PharmacText>
              </ExtranetContainer>
            </ScheduleSlot>
          )
        })
      ) : (
        <>
          {minDesktop ? (
            <PharmacBadge>{t('extranet:customerGroupSettings.noSelectableTimes')}</PharmacBadge>
          ) : (
            <></>
          )}
        </>
      )}
    </Day>
  )
}
